import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Seo from "../components/Seo";
import { adminCreateProject, adminGetProject, adminMe, adminUpdateProject, fetchSettings } from "../lib/api";
import { MediaPicker } from "../components/MediaPicker";
import { AdminHeader } from "../components/AdminNav";

const slugify = (s) =>
    (s || "")
        .toLowerCase()
        .replace(/[^\p{L}\p{N}\s-]/gu, "")
        .trim()
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");

const blank = {
    slug: "",
    title: "",
    category: "",
    primary_category: "",
    categories: [],
    location: "",
    year: new Date().getFullYear(),
    size: "",
    client: "",
    industry: "",
    image: "",
    image_focal: null,
    hero_image: "",
    hero_image_focal: null,
    gallery: ["", "", "", "", ""],
    gallery_focals: [null, null, null, null, null],
    scope: [""],
    services: [],
    awards: [],
    summary: "",
    story: "",
    tags: [],
};

export default function AdminProjectEditor() {
    const { slug: editSlug } = useParams();
    const isEdit = Boolean(editSlug);
    const navigate = useNavigate();
    const [form, setForm] = useState(blank);
    const [tagsText, setTagsText] = useState("");
    const [scopeText, setScopeText] = useState("");
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [projectCategories, setProjectCategories] = useState([]);
    const [projectServices, setProjectServices] = useState([]);

    useEffect(() => {
        adminMe().catch(() => navigate("/admin/login", { replace: true }));
    }, [navigate]);

    useEffect(() => {
        fetchSettings()
            .then((s) => {
                setProjectCategories(s?.project_categories || []);
                setProjectServices(s?.project_services || []);
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!isEdit) return;
        adminGetProject(editSlug)
            .then((p) => {
                const cats = (p.categories && p.categories.length > 0)
                    ? p.categories
                    : (p.primary_category ? [p.primary_category] : []);
                const safe = {
                    ...blank,
                    ...p,
                    gallery: [...(p.gallery || []), "", "", "", "", ""].slice(0, 5),
                    gallery_focals: [...(p.gallery_focals || []), null, null, null, null, null].slice(0, 5),
                    scope: (p.scope && p.scope.length ? p.scope : [""]),
                    services: p.services || [],
                    awards: p.awards || [],
                    categories: cats,
                    primary_category: cats[0] || "",
                };
                setForm(safe);
                setTagsText((p.tags || []).join(", "));
                setScopeText((p.scope || [""]).join("\n"));
            })
            .catch((err) => setError(err?.response?.data?.detail || "Could not load project"))
            .finally(() => setLoading(false));
    }, [isEdit, editSlug]);

    const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
    const setGallery = (idx, value) => {
        const next = [...form.gallery];
        next[idx] = value;
        setForm((f) => ({ ...f, gallery: next }));
    };
    const setGalleryFocal = (idx, focal) => {
        const next = [...(form.gallery_focals || [])];
        while (next.length < 5) next.push(null);
        next[idx] = focal;
        setForm((f) => ({ ...f, gallery_focals: next }));
    };
    const toggleService = (svc) => {
        setForm((f) => {
            const has = f.services.includes(svc);
            return {
                ...f,
                services: has ? f.services.filter((s) => s !== svc) : [...f.services, svc],
            };
        });
    };
    const toggleCategory = (cat) => {
        setForm((f) => {
            const has = f.categories.includes(cat);
            const next = has ? f.categories.filter((c) => c !== cat) : [...f.categories, cat];
            return { ...f, categories: next, primary_category: next[0] || "" };
        });
    };
    const setAward = (i, key, value) =>
        setForm((f) => {
            const next = [...(f.awards || [])];
            next[i] = { ...next[i], [key]: value };
            return { ...f, awards: next };
        });
    const addAward = () =>
        setForm((f) => ({
            ...f,
            awards: [...(f.awards || []), { logo: "", name: "", organization: "", year: "", link: "" }],
        }));
    const removeAward = (i) =>
        setForm((f) => ({ ...f, awards: (f.awards || []).filter((_, idx) => idx !== i) }));
    const moveAward = (i, dir) =>
        setForm((f) => {
            const next = [...(f.awards || [])];
            const j = i + dir;
            if (j < 0 || j >= next.length) return f;
            [next[i], next[j]] = [next[j], next[i]];
            return { ...f, awards: next };
        });

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setSaving(true);
        try {
            const cats = form.categories || [];
            const payload = {
                ...form,
                slug: form.slug || slugify(form.title),
                year: Number(form.year),
                gallery: form.gallery.map((g) => g.trim()).filter(Boolean),
                // Keep gallery_focals aligned to non-empty gallery URLs.
                gallery_focals: form.gallery
                    .map((g, i) => (g.trim() ? (form.gallery_focals?.[i] || null) : null))
                    .filter((_, i) => form.gallery[i].trim()),
                scope: scopeText.split("\n").map((s) => s.trim()).filter(Boolean),
                tags: tagsText.split(",").map((t) => t.trim()).filter(Boolean),
                industry: form.industry || null,
                categories: cats,
                primary_category: cats[0] || null,
                services: form.services || [],
                awards: (form.awards || []).filter(
                    (a) => a.name || a.organization || a.logo || a.year
                ),
            };
            if (isEdit) {
                await adminUpdateProject(editSlug, payload);
            } else {
                await adminCreateProject(payload);
            }
            navigate("/admin/projects");
        } catch (err) {
            setError(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <main className="min-h-screen bg-[#F9F8F6] px-6 md:px-12 lg:px-24 py-16">
                <p className="asc-overline">Loading…</p>
            </main>
        );
    }

    return (
        <main data-testid="page-admin-project-editor" className="min-h-screen bg-[#F9F8F6] px-6 md:px-12 lg:px-24 py-16 md:py-24">
            <Seo
                path={isEdit ? `/admin/projects/${editSlug}/edit` : "/admin/projects/new"}
                title={isEdit ? "Edit project" : "New project"}
                description="Project editor"
            />
            <AdminHeader title={isEdit ? "Edit project" : "New project"} />

            {error && <p className="mt-6 text-red-700 text-sm" data-testid="editor-error">{error}</p>}

            <form onSubmit={submit} className="mt-10 max-w-3xl space-y-8">
                <Section title="Identity">
                    <Field label="Title" required>
                        <input
                            data-testid="field-title"
                            value={form.title}
                            onChange={(e) => {
                                set("title", e.target.value);
                                if (!isEdit) set("slug", slugify(e.target.value));
                            }}
                            required
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Slug" hint="URL: /projects/[slug]">
                        <input
                            data-testid="field-slug"
                            value={form.slug}
                            onChange={(e) => set("slug", slugify(e.target.value))}
                            required
                            className={inputCls}
                        />
                    </Field>
                </Section>

                <Section title="Project facts">
                    <Field label="Client / Operator">
                        <input data-testid="field-client" value={form.client} onChange={(e) => set("client", e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="Location">
                        <input data-testid="field-location" value={form.location} onChange={(e) => set("location", e.target.value)} className={inputCls} required />
                    </Field>
                    <Field label="Typology (free-text)" hint="Free text shown on the project detail. e.g., 'City Hotel' or 'Countryside Resort'.">
                        <input data-testid="field-category" value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls} required />
                    </Field>
                    <Field label="Categories" required hint="Choose one or more. Drives the Projects index filters. Manage the master list in Settings.">
                        <div data-testid="field-categories" className="flex flex-wrap gap-2">
                            {projectCategories.length === 0 ? (
                                <p className="text-xs text-[#8C7A6B]">No categories configured yet — add them in Settings.</p>
                            ) : (
                                projectCategories.map((c) => {
                                    const on = form.categories.includes(c);
                                    return (
                                        <button
                                            type="button"
                                            key={c}
                                            data-testid={`category-toggle-${slugify(c)}`}
                                            onClick={() => toggleCategory(c)}
                                            className={`px-3 py-2 text-[11px] uppercase tracking-[0.18em] border transition-colors ${
                                                on
                                                    ? "bg-[#1A1A1A] text-[#F9F8F6] border-[#1A1A1A]"
                                                    : "bg-white text-[#5C5855] border-[#E5E1DB] hover:border-[#1A1A1A]"
                                            }`}
                                        >
                                            {c}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </Field>
                    <Field label="Industry" hint="Optional — only shown when filled">
                        <input data-testid="field-industry" value={form.industry || ""} onChange={(e) => set("industry", e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="Size">
                        <input data-testid="field-size" value={form.size} onChange={(e) => set("size", e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="Year">
                        <input data-testid="field-year" type="number" min="1900" max="2100" value={form.year} onChange={(e) => set("year", e.target.value)} className={inputCls} required />
                    </Field>
                    <Field label="Services" hint="Multi-select — shown on the project's Facts panel. Manage the master list in Settings.">
                        <div data-testid="field-services" className="flex flex-wrap gap-2">
                            {projectServices.length === 0 ? (
                                <p className="text-xs text-[#8C7A6B]">No services configured yet — add them in Settings.</p>
                            ) : (
                                projectServices.map((svc) => {
                                    const on = form.services.includes(svc);
                                    return (
                                        <button
                                            type="button"
                                            key={svc}
                                            data-testid={`service-toggle-${slugify(svc)}`}
                                            onClick={() => toggleService(svc)}
                                            className={`px-3 py-2 text-[11px] uppercase tracking-[0.18em] border transition-colors ${
                                                on
                                                    ? "bg-[#1A1A1A] text-[#F9F8F6] border-[#1A1A1A]"
                                                    : "bg-white text-[#5C5855] border-[#E5E1DB] hover:border-[#1A1A1A]"
                                            }`}
                                        >
                                            {svc}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </Field>
                    <Field label="Scope" hint="One line per scope item">
                        <textarea
                            data-testid="field-scope"
                            value={scopeText}
                            onChange={(e) => setScopeText(e.target.value)}
                            rows={4}
                            className={inputCls}
                        />
                    </Field>
                </Section>

                <Section title="Narrative">
                    <Field label="Approach question / quote" hint="Italic question shown above narrative">
                        <textarea data-testid="field-summary" value={form.summary} onChange={(e) => set("summary", e.target.value)} rows={2} className={inputCls} />
                    </Field>
                    <Field label="Story" hint="Use blank line for paragraphs">
                        <textarea data-testid="field-story" value={form.story} onChange={(e) => set("story", e.target.value)} rows={12} className={inputCls} />
                    </Field>
                </Section>

                <Section title="Imagery">
                    <MediaPicker
                        label="Cover image (project cards + Open Graph)"
                        testid="cover"
                        value={form.image}
                        onChange={(url) => set("image", url)}
                        focal={form.image_focal}
                        onFocalChange={(focal) => set("image_focal", focal)}
                        required
                    />
                    <MediaPicker
                        label="Hero background (Project page hero)"
                        testid="hero"
                        value={form.hero_image}
                        onChange={(url) => set("hero_image", url)}
                        focal={form.hero_image_focal}
                        onFocalChange={(focal) => set("hero_image_focal", focal)}
                    />
                    {form.gallery.map((g, i) => (
                        <MediaPicker
                            key={i}
                            label={`Gallery image ${i + 1}`}
                            testid={`gallery-${i}`}
                            value={g}
                            onChange={(url) => setGallery(i, url)}
                            focal={form.gallery_focals?.[i]}
                            onFocalChange={(focal) => setGalleryFocal(i, focal)}
                        />
                    ))}
                </Section>

                <Section title="Awards">
                    <p className="text-sm text-[#5C5855] max-w-xl -mt-2">
                        Optional. Displayed as a dedicated section on the public project page below the narrative. Add one row per award, upload a logo, and reorder with ↑/↓.
                    </p>
                    {(form.awards || []).map((a, i) => (
                        <div
                            key={i}
                            data-testid={`award-row-${i}`}
                            className="border border-[#E5E1DB] bg-white p-5 space-y-4"
                        >
                            <div className="flex items-center justify-between">
                                <p className="asc-overline text-[#8C7A6B]">Award {i + 1}</p>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => moveAward(i, -1)}
                                        disabled={i === 0}
                                        data-testid={`award-up-${i}`}
                                        className="text-xs text-[#5C5855] disabled:opacity-30 px-2 py-1"
                                    >
                                        ↑
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => moveAward(i, 1)}
                                        disabled={i === form.awards.length - 1}
                                        data-testid={`award-down-${i}`}
                                        className="text-xs text-[#5C5855] disabled:opacity-30 px-2 py-1"
                                    >
                                        ↓
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => removeAward(i)}
                                        data-testid={`award-remove-${i}`}
                                        className="text-[10px] uppercase tracking-[0.18em] text-red-700 px-2 py-1"
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                            <MediaPicker
                                label="Award logo"
                                testid={`award-logo-${i}`}
                                value={a.logo}
                                onChange={(url) => setAward(i, "logo", url)}
                            />
                            <Field label="Award name">
                                <input
                                    data-testid={`award-name-${i}`}
                                    value={a.name}
                                    onChange={(e) => setAward(i, "name", e.target.value)}
                                    className={inputCls}
                                    placeholder="Winner / Highly Commended / Shortlist"
                                />
                            </Field>
                            <Field label="Organisation">
                                <input
                                    data-testid={`award-org-${i}`}
                                    value={a.organization}
                                    onChange={(e) => setAward(i, "organization", e.target.value)}
                                    className={inputCls}
                                    placeholder="e.g. International Property Awards"
                                />
                            </Field>
                            <Field label="Year">
                                <input
                                    data-testid={`award-year-${i}`}
                                    value={a.year}
                                    onChange={(e) => setAward(i, "year", e.target.value)}
                                    className={inputCls}
                                    placeholder="e.g. 2025"
                                />
                            </Field>
                            <Field label="Link (optional)" hint="Full URL — clickable from the public page">
                                <input
                                    data-testid={`award-link-${i}`}
                                    value={a.link}
                                    onChange={(e) => setAward(i, "link", e.target.value)}
                                    className={inputCls}
                                    placeholder="https://…"
                                />
                            </Field>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={addAward}
                        data-testid="award-add"
                        className="px-4 py-2 text-[11px] uppercase tracking-[0.22em] border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#F9F8F6]"
                    >
                        + Add award
                    </button>
                </Section>

                <Section title="Discovery">
                    <Field label="Tags" hint="Comma-separated">
                        <input data-testid="field-tags" value={tagsText} onChange={(e) => setTagsText(e.target.value)} className={inputCls} />
                    </Field>
                </Section>

                <div className="flex items-center gap-6 pt-4 border-t border-[#E5E1DB]">
                    <button
                        type="submit"
                        data-testid="editor-save"
                        disabled={saving}
                        className="px-8 py-3 bg-[#1A1A1A] text-[#F9F8F6] text-[11px] uppercase tracking-[0.22em] hover:bg-black disabled:opacity-50"
                    >
                        {saving ? "Saving…" : isEdit ? "Save changes" : "Create project"}
                    </button>
                    <Link
                        to="/admin/projects"
                        className="text-[11px] uppercase tracking-[0.22em] text-[#5C5855] hover:text-[#1A1A1A]"
                    >
                        Cancel
                    </Link>
                </div>
            </form>
        </main>
    );
}

const inputCls = "w-full bg-white border border-[#E5E1DB] focus:border-[#1A1A1A] outline-none px-4 py-3 text-sm font-mono text-[#1A1A1A]";

const Section = ({ title, children }) => (
    <section>
        <p className="asc-overline mb-4">{title}</p>
        <div className="space-y-5">{children}</div>
    </section>
);

const Field = ({ label, hint, required, children }) => (
    <label className="block">
        <span className="block text-[10px] uppercase tracking-[0.22em] text-[#8C7A6B] mb-2">
            {label}{required && <span className="text-red-700"> *</span>}
            {hint && <span className="ml-2 lowercase tracking-normal text-[#A89F94] normal-case">— {hint}</span>}
        </span>
        {children}
    </label>
);
